import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApiError } from "@/api/problem";
import { UpgradeLink } from "@/features/billing/UpgradeLink";
import { Button, Field, Input, Notice } from "@/components/ui/Field";
import { fieldMessage } from "@/lib/fieldMessage";
import { meQuery } from "@/features/auth/session";
import { SettingsHeader } from "@/features/settings/SettingsHeader";
import {
  businessesQuery,
  useCreateBusiness,
  useUpdateBusiness,
  type Business,
} from "@/features/settings/queries";

const schema = z.object({
  name: z.string().trim().min(1, "settings.errors.nameRequired").max(120),
});
type Values = z.infer<typeof schema>;

export function BusinessesPage() {
  const { t } = useTranslation();
  const businesses = useQuery(businessesQuery);
  const create = useCreateBusiness();
  // The error carries the plan code; `me` carries its display name, which is what to show.
  const me = useQuery(meQuery);
  const [capReached, setCapReached] = useState<{ cap: number } | undefined>();

  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: "" } });

  const submit = form.handleSubmit(async (values) => {
    setCapReached(undefined);
    try {
      await create.mutateAsync(values.name);
      form.reset({ name: "" });
    } catch (error) {
      if (!(error instanceof ApiError)) {
        form.setError("name", { type: "server", message: t("settings.errors.saveFailed") });
        return;
      }
      if (error.isReadOnly) {
        form.setError("name", { type: "server", message: t("common.readOnly") });
        return;
      }
      // The plan's limit is not a form error; it is an offer to upgrade.
      if (error.isEntitlement) {
        setCapReached({ cap: Number(error.member("cap") ?? 0) });
        return;
      }
      form.setError("name", { type: "server", message: error.message });
    }
  });

  return (
    <section className="space-y-5">
      <SettingsHeader
        title={t("settings.businesses.title")}
        description={t("settings.businesses.subtitle")}
      />

      {businesses.isPending && <p className="text-stone-500">{t("common.loading")}</p>}
      {businesses.isError && <Notice tone="error">{t("settings.errors.loadFailed")}</Notice>}

      {businesses.data && businesses.data.length > 0 && (
        <ul className="divide-y divide-stone-200 overflow-hidden rounded-lg border border-stone-200 bg-white">
          {businesses.data.map((business) => (
            <BusinessRow key={business.id} business={business} />
          ))}
        </ul>
      )}

      {businesses.data?.length === 0 && (
        <Notice tone="info">{t("settings.businesses.empty")}</Notice>
      )}

      {capReached ? (
        <Notice tone="info">
          {t("settings.businesses.capReached", {
            cap: capReached.cap,
            plan: me.data?.workspace?.plan_name ?? "",
          })}{" "}
          <UpgradeLink />
        </Notice>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-3">
          <Field
            name="newBusiness"
            label={t("settings.businesses.addLabel")}
            error={fieldMessage(form.formState.errors, "name", t)}
          >
            <Input
              id="newBusiness"
              placeholder={t("settings.businesses.addPlaceholder")}
              invalid={!!form.formState.errors.name}
              {...form.register("name")}
            />
          </Field>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t("common.saving") : t("settings.businesses.add")}
          </Button>
        </form>
      )}
    </section>
  );
}

function BusinessRow({ business }: { business: Business }) {
  const { t } = useTranslation();
  const update = useUpdateBusiness();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(business.name);
  const [error, setError] = useState<string | undefined>();

  async function save() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === business.name) {
      setEditing(false);
      setName(business.name);
      return;
    }
    setError(undefined);
    try {
      await update.mutateAsync({ id: business.id, name: trimmed });
      setEditing(false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("settings.errors.saveFailed"));
    }
  }

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex items-center gap-3">
        {editing ? (
          <Input
            value={name}
            autoFocus
            aria-label={t("settings.businesses.nameOf", { name: business.name })}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void save();
              if (event.key === "Escape") {
                setEditing(false);
                setName(business.name);
              }
            }}
          />
        ) : (
          <span
            className={
              business.is_active ? "flex-1 font-medium" : "flex-1 font-medium text-stone-400"
            }
          >
            {business.name}
            {!business.is_active && (
              <span className="ml-2 text-xs font-normal">{t("settings.businesses.inactive")}</span>
            )}
          </span>
        )}
        {editing ? (
          <Button type="button" className="w-auto px-3" onClick={() => void save()}>
            {t("common.save")}
          </Button>
        ) : (
          <button
            type="button"
            className="text-brand-700 text-sm underline"
            onClick={() => setEditing(true)}
          >
            {t("common.rename")}
          </button>
        )}
      </div>

      {!editing && (
        <button
          type="button"
          disabled={update.isPending}
          className="text-sm text-stone-500 underline"
          onClick={() => update.mutate({ id: business.id, is_active: !business.is_active })}
        >
          {business.is_active
            ? t("settings.businesses.deactivate")
            : t("settings.businesses.reactivate")}
        </button>
      )}

      {error && <Notice tone="error">{error}</Notice>}
    </li>
  );
}
